import React, { useState, useEffect, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Printer, RefreshCw, Minus, Plus, Tag, Crosshair, FileDown } from 'lucide-react';
import { jsPDF } from 'jspdf';
import api from '../api';

const TRACKING_URL = 'https://expressway.xcourier.co.za/track';

// A4 dimensions in mm
const A4_W = 210;
const A4_H = 297;

// Common label sizes (width × height in mm)
const PRESETS = [
  // Our sticker sheet on the office printer (measured from a scan, 26 Sep 2026):
  // the printer lands ~3.7 mm high and the rows are 30.06 mm apart.
  { label: '39 × 30',  w: 39,  h: 30,
    layout: { cols: 5, rows: 9, gap: 0, rowGap: 0.06, marginTop: 17.2, marginLeft: 7.5 } },
  { label: '40 × 30',  w: 40,  h: 30  },
  { label: '50 × 30',  w: 50,  h: 30  },
  { label: '50 × 25',  w: 50,  h: 25  },
  { label: '57 × 32',  w: 57,  h: 32  },
  { label: '100 × 50', w: 100, h: 50  },
  { label: '100 × 150', w: 100, h: 150 },
];

const r1 = v => Math.round(v * 10) / 10;
const r2 = v => Math.round(v * 100) / 100;

// The sheet layout that last printed well, remembered on this computer so
// nobody has to re-measure the sticker paper every time.
const LAYOUT_KEY = 'label_layout_v2';
function savedLayout() {
  try { return JSON.parse(localStorage.getItem(LAYOUT_KEY) || 'null'); } catch { return null; }
}

// How many columns/rows of (w×h, separated by gap) fit on an A4 page,
// and the margins needed to centre that block on the page.
function fitA4(w, h, colGap, rowGap = colGap, wantRows = null) {
  const cols = Math.max(1, Math.floor((A4_W + colGap) / (w + colGap)));
  const rows = wantRows || Math.max(1, Math.floor((A4_H + rowGap) / (h + rowGap)));
  const usedW = cols * w + (cols - 1) * colGap;
  const usedH = rows * h + (rows - 1) * rowGap;
  return {
    cols, rows,
    marginLeft: Math.max(0, r1((A4_W - usedW) / 2)),
    marginTop:  Math.max(0, r1((A4_H - usedH) / 2)),
  };
}

// Minimal PNG writer for a 1-bit greyscale image (no alpha channel), used for
// the QR codes in the downloaded PDF.
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();
function crc32(bytes) {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}
function pngChunk(type, data) {
  const out = new Uint8Array(12 + data.length);
  const dv = new DataView(out.buffer);
  dv.setUint32(0, data.length);
  for (let i = 0; i < 4; i++) out[4 + i] = type.charCodeAt(i);
  out.set(data, 8);
  dv.setUint32(8 + data.length, crc32(out.subarray(4, 8 + data.length)));
  return out;
}
async function canvasToPng1bit(canvas) {
  const w = canvas.width, h = canvas.height;
  const px = canvas.getContext('2d').getImageData(0, 0, w, h).data;
  const rowBytes = Math.ceil(w / 8);
  const raw = new Uint8Array((rowBytes + 1) * h);            // filter byte 0 per row
  for (let y = 0; y < h; y++) {
    const row = y * (rowBytes + 1);
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const lum = (px[i] * 299 + px[i + 1] * 587 + px[i + 2] * 114) / 1000;
      const white = px[i + 3] < 128 || lum >= 128;
      if (white) raw[row + 1 + (x >> 3)] |= 0x80 >> (x & 7);
    }
  }
  const zlib = new Uint8Array(await new Response(
    new Blob([raw]).stream().pipeThrough(new CompressionStream('deflate'))).arrayBuffer());
  const ihdr = new Uint8Array(13);
  const dv = new DataView(ihdr.buffer);
  dv.setUint32(0, w); dv.setUint32(4, h);
  ihdr[8] = 1; ihdr[9] = 0; ihdr[10] = 0; ihdr[11] = 0; ihdr[12] = 0;   // 1-bit greyscale
  const parts = [new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]),
    pngChunk('IHDR', ihdr), pngChunk('IDAT', zlib), pngChunk('IEND', new Uint8Array(0))];
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let o = 0;
  for (const part of parts) { out.set(part, o); o += part.length; }
  return out;
}

export default function BarcodeGenerator() {
  const saved = useRef(savedLayout()).current;
  const [count,     setCount]     = useState(45);
  const [w,         setW]         = useState(saved?.w ?? 39);   // label width  (mm) — default 39×30 sheet
  const [h,         setH]         = useState(saved?.h ?? 30);   // label height (mm)
  const [mode,      setMode]      = useState(saved?.mode ?? 'sheet'); // 'roll' | 'sheet'
  const [showName,  setShowName]  = useState(saved?.showName ?? true);  // show "EXPRESSWAY COURIER" line
  const [showBorder, setShowBorder] = useState(saved?.showBorder ?? true); // print cut-line guides

  // A4 sheet layout (mm) — auto-centred, adjustable
  const [cols,      setCols]      = useState(saved?.cols ?? 5);
  const [gap,       setGap]       = useState(saved?.gap ?? 0);        // between columns
  const [rowGap,    setRowGap]    = useState(saved?.rowGap ?? 0.06);  // between rows
  const [rows,      setRows]      = useState(saved?.rows ?? 9);
  const [marginTop, setMarginTop] = useState(saved?.marginTop ?? 17.2);
  const [marginLeft, setMarginLeft] = useState(saved?.marginLeft ?? 7.5);

  // Printer correction. The fields above describe the STICKER SHEET; most
  // printers then shrink or stretch the page slightly (and shift it), so row 1
  // lands but row 9 is millimetres off. Measured once from a plain-paper test
  // print: printed = offset + scale × page, per axis. We draw at
  // (sheet − offset) / scale so the print lands on the sheet.
  const [corr, setCorr] = useState(saved?.corr ?? { sx: 1, sy: 1, ox: 0, oy: 0 });

  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_KEY, JSON.stringify({
        w, h, mode, showName, showBorder, cols, rows, gap, rowGap, marginTop, marginLeft, corr }));
    } catch { /* private window — just don't remember */ }
  }, [w, h, mode, showName, showBorder, cols, rows, gap, rowGap, marginTop, marginLeft, corr]);

  // What is actually drawn on the page (mm), after the printer correction
  const drawTop    = (marginTop  - corr.oy) / corr.sy;
  const drawLeft   = (marginLeft - corr.ox) / corr.sx;
  const drawRowP   = (h + rowGap) / corr.sy;   // row pitch
  const drawColP   = (w + gap)    / corr.sx;   // column pitch

  // Measurements typed in from the plain-paper test print
  const [meas, setMeas] = useState({ top: '', span: '', left: '', cspan: '' });
  const applyCorrection = () => {
    const n = (v) => parseFloat(String(v).replace(',', '.'));
    const next = { ...corr };
    // Vertical: top of paper → top line of row 1, and row 1 → last row
    if (rows > 1 && n(meas.span) > 0) {
      const sy = n(meas.span) / ((rows - 1) * drawRowP);
      next.sy = sy;
      next.oy = n(meas.top) >= 0 && meas.top !== '' ? n(meas.top) - sy * drawTop : corr.oy;
    }
    if (cols > 1 && n(meas.cspan) > 0) {
      const sx = n(meas.cspan) / ((cols - 1) * drawColP);
      next.sx = sx;
      next.ox = n(meas.left) >= 0 && meas.left !== '' ? n(meas.left) - sx * drawLeft : corr.ox;
    }
    if (next.sy < 0.8 || next.sy > 1.2 || next.sx < 0.8 || next.sx > 1.2) {
      alert('Those measurements are more than 20% out — check them and try again.');
      return;
    }
    setCorr(next);
    setMeas({ top: '', span: '', left: '', cspan: '' });
  };
  const corrActive = Math.abs(corr.sx - 1) > 1e-4 || Math.abs(corr.sy - 1) > 1e-4 || corr.ox || corr.oy;

  const [numbers,   setNumbers]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [generated, setGenerated] = useState(false);

  // Reprint mode: Label Tracking sends the damaged numbers through in the URL
  // after logging and emailing the reprint. Nothing is issued here — these
  // numbers already exist — so the issuing controls are hidden and only the
  // size/layout settings and the print button remain.
  const reprintList = new URLSearchParams(window.location.search).get('reprint');
  const isReprint = !!reprintList;
  useEffect(() => {
    if (!reprintList) return;
    // "batch" means the numbers were handed over in storage — a few hundred
    // labels would make an unreasonably long URL. A comma list still works for
    // a handful.
    let list;
    if (reprintList === 'batch') {
      try { list = JSON.parse(localStorage.getItem('reprint_batch') || '[]'); }
      catch { list = []; }
    } else {
      // Comma list, where an item may be a range: PCS0001-PCS0380 (or PCS0001-0380)
      list = [];
      for (const part of reprintList.split(',')) {
        const m = part.trim().toUpperCase().match(/^([A-Z]+)(\d+)-(?:([A-Z]+))?(\d+)$/);
        // Either direction: PCS0270-PCS0001 prints 0270 first, counting down.
        if (m && (!m[3] || m[3] === m[1]) && Math.abs(+m[4] - +m[2]) < 1000) {
          const width = m[2].length;
          const step = +m[4] >= +m[2] ? 1 : -1;
          for (let n = +m[2]; n !== +m[4] + step; n += step) list.push(m[1] + String(n).padStart(width, '0'));
        } else {
          list.push(part);
        }
      }
    }
    setNumbers((list || []).map((s) => String(s).trim().toUpperCase()).filter(Boolean));
    setGenerated(true);
  }, [reprintList]);

  // Holder (driver or Office) + editable prefix
  const [drivers,  setDrivers]  = useState([]);
  const [holderId, setHolderId] = useState('');   // driver id, or 'office'
  const [prefix,   setPrefix]   = useState('');
  const [issuedRange, setIssuedRange] = useState('');

  useEffect(() => {
    api.get('/users/drivers').then(r => setDrivers(r.data)).catch(() => {});
  }, []);

  // When the holder changes, prefill the prefix with the first 3 letters
  const holderName = holderId === 'office'
    ? 'Office'
    : (drivers.find(d => d.id === holderId)?.name || '');
  const selectHolder = (id) => {
    setHolderId(id);
    const name = id === 'office' ? 'Office' : (drivers.find(d => d.id === id)?.name || '');
    setPrefix(name.replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase());
    setNumbers([]); setGenerated(false); setIssuedRange('');
  };

  // Re-centre the sheet whenever the label size changes — but not on the first
  // render, or a remembered, hand-tuned layout would be thrown away.
  const firstSize = useRef(true);
  const presetApplied = useRef(false);   // a preset button already set the layout
  useEffect(() => {
    if (firstSize.current) { firstSize.current = false; return; }
    if (presetApplied.current) { presetApplied.current = false; return; }
    const fit = fitA4(w, h, 0, 0);
    setGap(0);
    setRowGap(0);
    setRows(fit.rows);
    setCols(fit.cols);
    setMarginLeft(fit.marginLeft);
    setMarginTop(fit.marginTop);
  }, [w, h]); // eslint-disable-line react-hooks/exhaustive-deps

  // A preset sets the size and, when it knows its sheet, the whole layout;
  // otherwise the layout is centred on A4.
  const applyPreset = (p) => {
    const f = fitA4(p.w, p.h, 0, 0);
    const L = { cols: f.cols, rows: f.rows, gap: 0, rowGap: 0,
      marginTop: f.marginTop, marginLeft: f.marginLeft, ...(p.layout || {}) };
    if (p.w !== w || p.h !== h) presetApplied.current = true;
    setW(p.w); setH(p.h);
    setCols(L.cols); setRows(L.rows); setGap(L.gap); setRowGap(L.rowGap);
    setMarginTop(L.marginTop); setMarginLeft(L.marginLeft);
  };

  const autoCentre = () => {
    const fit = fitA4(w, h, gap, rowGap, rows);
    setCols(fit.cols);
    setMarginLeft(fit.marginLeft);
    setMarginTop(fit.marginTop);
  };

  const issue = async () => {
    if (!holderId) { alert('Choose a driver or Office first.'); return; }
    if (!prefix.trim()) { alert('Enter a prefix.'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/barcodes/issue', {
        driver_id: holderId === 'office' ? null : holderId,
        holder_name: holderName,
        prefix: prefix.trim(),
        count,
      });
      setNumbers(data.tracking_numbers);
      setGenerated(true);
      if (data.tracking_numbers.length !== count)
        alert(`Asked for ${count} labels but ${data.tracking_numbers.length} were issued.`);
      const list = data.tracking_numbers;
      setIssuedRange(list.length ? `${list[0]} – ${list[list.length - 1]}` : '');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to issue barcodes. Check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const print = () => window.print();

  // The office printer drops labels when one job holds more than a couple of
  // sheets, so long runs are printed in batches: only the chosen sheets are
  // printed, the rest are hidden from that one print.
  const [batchSize, setBatchSize] = useState(() => {
    try { return parseInt(localStorage.getItem('label_batch_size'), 10) || 2; } catch { return 2; }
  });
  const [printRange, setPrintRange] = useState(null);   // [firstSheet, lastSheet], 0-based
  const [printedBatches, setPrintedBatches] = useState({});
  useEffect(() => { try { localStorage.setItem('label_batch_size', String(batchSize)); } catch { /* ignore */ } }, [batchSize]);
  useEffect(() => { setPrintedBatches({}); }, [numbers]);
  const printBatch = (from, to) => {
    setPrintRange([from, to]);
    // let React hide the other sheets before the print dialog snapshots the page
    setTimeout(() => {
      window.print();
      setPrintedBatches((b) => ({ ...b, [from]: true }));
      setPrintRange(null);
    }, 80);
  };

  // Decimals matter on sticker sheets: 0.4 mm too much
  // per row is 3 mm by the bottom of the page.
  const setWidth  = v => setW(Math.min(210, Math.max(10, r2(parseFloat(v) || 0))));
  const setHeight = v => setH(Math.min(297, Math.max(10, r2(parseFloat(v) || 0))));
  const activePreset = PRESETS.find(p => p.w === w && p.h === h);

  // Rows that fit per page + labels per A4 sheet
  const rowsPerPage = Math.max(1, rows);
  const perPage     = cols * rowsPerPage;

  // Adaptive internal sizing (mm)
  const pad       = Math.max(1.2, Math.min(w, h) * 0.06);
  const nameFont  = Math.max(1.3, Math.min(w * 0.05, 2.4));
  const qrMM      = Math.max(9, Math.min(w - pad * 2, (h - pad * 2) * (showName ? 0.62 : 0.74)));
  const trackFont = Math.max(1.7, Math.min(w * 0.08, 4.2));
  // QR codes are drawn as pictures, not vector paths. A page of 45 vector QR
  // codes overwhelmed the office printer (it stopped half-way down the page
  // and drew stray lines); a bitmap prints reliably. 224 px over ~16 mm is
  // about 350 dpi, several pixels per QR module.
  const qrPx      = 224;

  // Split labels into A4 pages (sheet mode)
  const pages = [];
  for (let i = 0; i < numbers.length; i += perPage) pages.push(numbers.slice(i, i + perPage));

  // The same sheet as a PDF file. Printing a saved PDF is the route that works
  // on the office Brother (printing the page straight from Chrome gives blank
  // and half pages over its Wi-Fi), so this skips the print dialog step.
  // Positions are the ones the page prints with, printer correction included.
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      await new Promise((r) => setTimeout(r, 30));   // let the button show "Making PDF…"
      // QR as a 1-bit black/white PNG. A canvas PNG always carries an alpha
      // channel, which the PDF stores as a transparency mask — and PDFs with
      // transparency are what printers mangle. 1-bit is also tiny and sharp.
      const qrImages = new Map();
      document.querySelectorAll('.label-card').forEach((card) => {
        const t = card.querySelector('.label-tracking')?.textContent;
        const c = card.querySelector('canvas');
        if (t && c && !qrImages.has(t)) qrImages.set(t, c);
      });
      for (const [t, c] of qrImages) qrImages.set(t, await canvasToPng1bit(c));

      // Take text sizes and positions from the label as the browser lays it
      // out, so the PDF matches the page (the PDF only has Helvetica/Courier,
      // the page uses the system's bold sans and monospace fonts).
      const PX = 25.4 / 96;                                   // CSS px → mm
      const card0 = document.querySelector('.label-card');
      const cardBox = card0.getBoundingClientRect();
      const textBox = (el) => {
        if (!el) return null;
        const rg = document.createRange(); rg.selectNodeContents(el);
        const r = rg.getBoundingClientRect();
        return { top: (r.top - cardBox.top) * PX, height: r.height * PX, width: r.width * PX };
      };
      const nameBox  = showName ? textBox(card0.querySelector('.label-company')) : null;
      const trackBox = textBox(card0.querySelector('.label-tracking'));
      const qrBox    = card0.querySelector('.label-qr canvas').getBoundingClientRect();
      const qrTop    = (qrBox.top - cardBox.top) * PX;
      const qrLeft   = (qrBox.left - cardBox.left) * PX;
      const qrSize   = qrBox.width * PX;
      const PT = 72 / 25.4;                                   // mm → pt
      // Font size that makes the PDF text as wide as the page's text, with the
      // same letter spacing the page uses (0.2 mm name, 0.25 mm number).
      const NAME_SPACE = 0.2, TRACK_SPACE = 0.25;
      const fitSize = (doc, text, font, style, widthMM, startMM, space) => {
        doc.setFont(font, style); doc.setFontSize(startMM * PT);
        const wNow = doc.getTextWidth(text);
        const target = Math.max(widthMM * 0.5, widthMM - space * text.length);
        return wNow > 0 ? startMM * PT * (target / wNow) : startMM * PT;
      };
      // left-aligned by hand: jsPDF's centring ignores the letter spacing
      const spacedText = (doc, text, cx, cy, space, extra = {}) => {
        const width = doc.getTextWidth(text) + space * text.length;
        doc.text(text, cx - width / 2, cy, { baseline: 'middle', charSpace: space, ...extra });
      };
      let nameSize = null, trackSize = null;

      const drawLabel = (doc, tracking, x, y) => {
        if (showBorder) {
          doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.2);
          doc.rect(x, y, w, h);
        }
        const cx = x + w / 2;
        if (nameBox) {
          if (nameSize == null) nameSize = fitSize(doc, 'EXPRESSWAY COURIER', 'helvetica', 'bold', nameBox.width, nameFont, NAME_SPACE);
          doc.setFont('helvetica', 'bold'); doc.setFontSize(nameSize); doc.setTextColor(249, 115, 22);
          spacedText(doc, 'EXPRESSWAY COURIER', cx, y + nameBox.top + nameBox.height / 2, NAME_SPACE);
        }
        const img = qrImages.get(tracking);
        if (img) doc.addImage(img, 'PNG', x + qrLeft, y + qrTop, qrSize, qrSize, `qr-${tracking}`);
        if (trackSize == null) trackSize = fitSize(doc, tracking, 'courier', 'bold', trackBox.width, trackFont, TRACK_SPACE);
        doc.setFont('courier', 'bold'); doc.setFontSize(trackSize); doc.setTextColor(17, 17, 17);
        // Courier Bold is thin next to the page's monospace; a hairline outline thickens it
        doc.setDrawColor(17, 17, 17); doc.setLineWidth(0.12);
        spacedText(doc, tracking, cx, y + trackBox.top + trackBox.height / 2, TRACK_SPACE, { renderingMode: 'fillThenStroke' });
      };

      let doc;
      if (mode === 'roll') {
        doc = new jsPDF({ unit: 'mm', format: [w, h], orientation: w > h ? 'landscape' : 'portrait' });
        numbers.forEach((t, i) => { if (i) doc.addPage([w, h], w > h ? 'landscape' : 'portrait'); drawLabel(doc, t, 0, 0); });
      } else {
        doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
        pages.forEach((page, pi) => {
          if (pi) doc.addPage('a4', 'portrait');
          page.forEach((t, i) => {
            const r = Math.floor(i / cols), c = i % cols;
            drawLabel(doc, t, Math.max(0, drawLeft) + c * drawColP, Math.max(0, drawTop) + r * drawRowP);
          });
        });
      }
      doc.save(`${numbers[0]}-${numbers[numbers.length - 1]}-labels.pdf`);
    } catch (err) {
      alert('Could not make the PDF: ' + (err.message || err));
    } finally {
      setPdfBusy(false);
    }
  };

  const renderCard = (tracking) => (
    <div key={tracking} className="label-card">
      {showName && <div className="label-company">EXPRESSWAY COURIER</div>}
      <div className="label-qr">
        <QRCodeCanvas value={`${TRACKING_URL}/${tracking}`} size={qrPx} level="H" includeMargin={false} />
      </div>
      <div className="label-tracking">{tracking}</div>
    </div>
  );

  return (
    <div>
      {/* ── Screen header (hidden when printing) ── */}
      <div className="no-print mb-6">
        <div className="flex items-center gap-3 mb-1">
          <Tag size={22} className="text-orange-500" />
          <h1 className="text-xl font-bold text-gray-800">Label Designer</h1>
        </div>
        <p className="text-sm text-gray-500 ml-9">
          {isReprint
            ? 'Reprinting existing labels — no new numbers are being issued. Set the size to match your sticker paper and print.'
            : 'Set your label size, generate unique tracking QR codes, and print them to fit your sticker roll or A4 sheet.'}
        </p>
      </div>

      {isReprint && (
        <div className="no-print bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <p className="text-sm font-semibold text-amber-900">
            Reprint — {numbers.length} existing label{numbers.length === 1 ? '' : 's'}
          </p>
          <p className="text-xs text-amber-800 mt-1">
            These numbers already exist and keep their holder and history. Once printed there will be
            two stickers for each one, so <strong>destroy the damaged originals</strong>. This reprint
            has been logged and the office has been emailed.
          </p>
        </div>
      )}

      {/* ── Controls (hidden when printing) ── */}
      <div className="no-print bg-white rounded-xl shadow-sm p-5 mb-6 space-y-5">

        {/* Issue to (holder) + prefix — not shown when reprinting, since
            nothing is being issued */}
        <div className={`bg-orange-50/60 border border-orange-100 rounded-lg p-4 ${isReprint ? 'hidden' : ''}`}>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Issue labels to</label>
          <div className="flex gap-3 flex-wrap items-end">
            <div>
              <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">Driver / Office</label>
              <select value={holderId} onChange={e => selectHolder(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
                <option value="">Select…</option>
                <option value="office">Office</option>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-semibold text-gray-400 uppercase mb-1">Prefix</label>
              <input type="text" value={prefix} maxLength={10}
                onChange={e => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                placeholder="ARC"
                className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono font-bold uppercase focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            {prefix && <span className="text-xs text-gray-500 pb-2.5">Labels will be <strong>{prefix}0001, {prefix}0002 …</strong></span>}
          </div>
        </div>

        {/* Label size */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Label size (mm)</label>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="number" value={w} min={10} max={210} step="0.1" onChange={e => setWidth(e.target.value)}
              className="w-20 text-center border border-gray-200 rounded-lg px-2 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-orange-400" />
            <span className="text-gray-400 font-semibold">×</span>
            <input type="number" value={h} min={10} max={297} step="0.1" onChange={e => setHeight(e.target.value)}
              className="w-20 text-center border border-gray-200 rounded-lg px-2 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-orange-400" />
            <span className="text-xs text-gray-400 ml-1">width × height</span>
          </div>
          <div className="flex gap-2 flex-wrap mt-3">
            {PRESETS.map(p => (
              <button key={p.label} onClick={() => applyPreset(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                  activePreset?.label === p.label
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500'
                }`}>
                {p.label} mm
              </button>
            ))}
          </div>
        </div>

        {/* Layout mode */}
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Print layout</label>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setMode('sheet')}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                mode === 'sheet' ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500'
              }`}>
              📄 A4 sticker sheet (grid)
            </button>
            <button onClick={() => setMode('roll')}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                mode === 'roll' ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500'
              }`}>
              🏷️ Label printer (one per page)
            </button>
          </div>
        </div>

        {/* A4 sheet alignment controls */}
        {mode === 'sheet' && (
          <div className="bg-orange-50/60 border border-orange-100 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">A4 sheet alignment (mm)</label>
              <button onClick={autoCentre} className="flex items-center gap-1.5 text-xs font-medium text-orange-600 hover:text-orange-700">
                <Crosshair size={13} /> Auto-centre
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
              <NumField label="Columns"      value={cols}       onChange={v => setCols(Math.max(1, parseInt(v) || 1))} />
              <NumField label="Rows"         value={rows}       onChange={v => setRows(Math.max(1, parseInt(v) || 1))} />
              <NumField label="Column gap"   value={gap}        step="0.1" onChange={v => setGap(Math.max(0, r2(parseFloat(v) || 0)))} />
              <NumField label="Row gap"      value={rowGap}     step="0.1" onChange={v => setRowGap(r2(parseFloat(v) || 0))} />
              <NumField label="Top margin"   value={marginTop}  step="0.1" onChange={v => setMarginTop(Math.max(0, r2(parseFloat(v) || 0)))} />
              <NumField label="Left margin"  value={marginLeft} step="0.1" onChange={v => setMarginLeft(Math.max(0, r2(parseFloat(v) || 0)))} />
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Fits <strong>{cols} × {rowsPerPage} = {perPage}</strong> labels per A4 sheet.
              {' '}Rows start every <strong>{r2(h + rowGap)} mm</strong>, columns every <strong>{r2(w + gap)} mm</strong>.
              {' '}These numbers are your <strong>sticker sheet</strong>, measured with a ruler.
            </p>

            <div className="mt-4 border-t border-orange-100 pt-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Printer correction</p>
                {corrActive ? (
                  <span className="text-xs text-green-700">
                    On — down the page {r2(corr.sy * 100)}%{corr.oy ? `, ${corr.oy > 0 ? '+' : ''}${r2(corr.oy)} mm` : ''}
                    {' '}· across {r2(corr.sx * 100)}%{corr.ox ? `, ${corr.ox > 0 ? '+' : ''}${r2(corr.ox)} mm` : ''}
                    <button onClick={() => setCorr({ sx: 1, sy: 1, ox: 0, oy: 0 })} className="ml-2 underline text-gray-500">reset</button>
                  </span>
                ) : <span className="text-xs text-gray-400">Off</span>}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                If row 1 lands but lower rows drift, the printer is resizing the page. Tick <strong>Cut-line guides</strong>,
                print <strong>one page on plain paper</strong>, measure the printout and type what you measure:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
                <NumField label="Paper top → row 1 line"            value={meas.top}   step="0.1" onChange={v => setMeas(m => ({ ...m, top: v }))} />
                <NumField label={`Row 1 → row ${rows} (top lines)`} value={meas.span}  step="0.1" onChange={v => setMeas(m => ({ ...m, span: v }))} />
                <NumField label="Paper left → col 1 line"           value={meas.left}  step="0.1" onChange={v => setMeas(m => ({ ...m, left: v }))} />
                <NumField label={`Col 1 → col ${cols} (left lines)`} value={meas.cspan} step="0.1" onChange={v => setMeas(m => ({ ...m, cspan: v }))} />
              </div>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <button onClick={applyCorrection} disabled={!meas.span && !meas.cspan}
                  className="bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                  Apply correction
                </button>
                <span className="text-[11px] text-gray-400">
                  A correct print measures {r2(marginTop)} mm, {r2((rows - 1) * (h + rowGap))} mm, {r2(marginLeft)} mm and {r2((cols - 1) * (w + gap))} mm.
                  After applying, print the test page again — it should now match.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Count + options */}
        <div className="flex flex-wrap items-end gap-4">
          <div className={isReprint ? 'hidden' : ''}>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Number of labels</label>
            <div className="flex items-center gap-2">
              <button onClick={() => setCount(c => Math.max(1, c - 1))} className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-gray-600"><Minus size={15} /></button>
              <input type="number" min={1} max={500} value={count}
                onChange={e => setCount(Math.min(500, Math.max(1, parseInt(e.target.value) || 1)))}
                className="w-16 text-center border border-gray-200 rounded-lg px-2 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-orange-400" />
              <button onClick={() => setCount(c => Math.min(500, c + 1))} className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 text-gray-600"><Plus size={15} /></button>
            </div>
          </div>

          {mode === 'sheet' && !isReprint && (
            <div className="flex gap-2">
              {[1, 2, 5].map(n => (
                <button key={n} onClick={() => setCount(perPage * n)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    count === perPage * n ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500'
                  }`}>
                  {n} sheet{n > 1 ? 's' : ''} ({perPage * n})
                </button>
              ))}
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer pb-1">
            <input type="checkbox" checked={showName} onChange={e => setShowName(e.target.checked)} className="accent-orange-500" />
            Company name
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer pb-1">
            <input type="checkbox" checked={showBorder} onChange={e => setShowBorder(e.target.checked)} className="accent-orange-500" />
            Cut-line guides
          </label>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-4 pt-1">
          {!isReprint && (
            <button onClick={issue} disabled={loading || !holderId}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors">
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Issuing…' : `Issue ${count} labels`}
            </button>
          )}
          {numbers.length > 0 && (
            <button onClick={print}
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-900 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors">
              <Printer size={15} /> Print {numbers.length} Labels
            </button>
          )}
          {numbers.length > 0 && (
            <button onClick={downloadPdf} disabled={pdfBusy}
              title="Save the labels as a PDF file, then open it and print at Actual size / 100%"
              className="flex items-center gap-2 bg-white border border-gray-800 text-gray-800 hover:bg-gray-50 disabled:opacity-50 px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors">
              <FileDown size={15} /> {pdfBusy ? 'Making PDF…' : 'Download PDF'}
            </button>
          )}
          {mode === 'sheet' && pages.length > batchSize && (
            <div className="w-full bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-2 flex-wrap text-xs text-gray-600 mb-2">
                <span className="font-semibold uppercase tracking-wide text-gray-500">Print in batches of</span>
                <select value={batchSize} onChange={(e) => setBatchSize(parseInt(e.target.value, 10))}
                  className="border border-gray-200 rounded px-2 py-1 text-xs">
                  {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} sheet{n > 1 ? 's' : ''}</option>)}
                </select>
                <span className="text-gray-400">— big jobs make the printer drop labels; print one batch, then the next.</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {Array.from({ length: Math.ceil(pages.length / batchSize) }, (_, bi) => {
                  const from = bi * batchSize;
                  const to = Math.min(pages.length - 1, from + batchSize - 1);
                  const first = pages[from][0];
                  const lastPage = pages[to];
                  const last = lastPage[lastPage.length - 1];
                  const done = printedBatches[from];
                  return (
                    <button key={from} onClick={() => printBatch(from, to)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                        done ? 'bg-green-50 border-green-300 text-green-800' : 'bg-white border-gray-300 text-gray-700 hover:border-gray-800'
                      }`}>
                      <Printer size={13} />
                      {done ? '✓ ' : ''}Sheets {from + 1}{to > from ? `–${to + 1}` : ''}{' '}
                      <span className="font-mono font-normal text-gray-500">({first} → {last})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {issuedRange && (
            <span className="text-sm text-green-700 font-medium">✅ Issued {issuedRange} to {holderName}</span>
          )}
        </div>

        {numbers.length > 0 && (
          <p className="text-xs text-gray-400">
            🖨️ In the print dialog, turn <strong>off “Headers and footers”</strong> and set <strong>Margins → None</strong>
            {mode === 'roll' && <>, then select your <strong>{w} × {h} mm</strong> label as the paper</>}.
            {mode === 'sheet' && <> Tip: print one sheet with cut-line guides on plain paper and hold it against your sticker sheet to check alignment.</>}
          </p>
        )}
      </div>

      {/* ── Output ── */}
      {numbers.length > 0 && (
        <>
          <p className="no-print text-xs text-gray-400 mb-2">
            Preview — actual size ({mode === 'sheet' ? `A4 sheet, ${w} × ${h} mm labels` : `${w} × ${h} mm labels`}):
          </p>

          {mode === 'roll' ? (
            <div className="label-grid mode-roll">
              {numbers.map(renderCard)}
            </div>
          ) : (
            <div className="sheet-wrap">
              {pages.map((page, pi) => (
                <div key={pi} className={`label-page${
                  printRange && (pi < printRange[0] || pi > printRange[1]) ? ' skip-print' : ''}${
                  pi === (printRange ? printRange[1] : pages.length - 1) ? ' print-last' : ''}`}>
                  <div className="label-grid mode-sheet">
                    {page.map(renderCard)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Empty state ── */}
      {!generated && (
        <div className="no-print flex flex-col items-center justify-center py-24 text-gray-400">
          <Tag size={48} className="mb-4 opacity-30" />
          <p className="text-lg font-medium">No labels generated yet</p>
          <p className="text-sm mt-1">Choose a driver/Office, set the count, and click Issue</p>
        </div>
      )}

      {/* ── Dynamic styles ── */}
      <style>{`
        .label-card {
          width: ${w}mm;
          height: ${h}mm;
          box-sizing: border-box;
          padding: ${pad}mm;
          background: #fff;
          border: 0.25mm dashed #e2e8f0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: ${pad * 0.45}mm;
          font-family: sans-serif;
          overflow: hidden;
        }
        .label-company {
          font-size: ${nameFont}mm; font-weight: 800; color: #f97316;
          letter-spacing: 0.2mm; text-align: center; line-height: 1.05; text-transform: uppercase;
        }
        .label-qr { display: flex; align-items: center; justify-content: center; }
        .label-qr canvas { width: ${qrMM}mm !important; height: ${qrMM}mm !important; display: block; image-rendering: pixelated; }
        .label-tracking {
          font-family: monospace; font-size: ${trackFont}mm; font-weight: 800;
          color: #111; letter-spacing: 0.25mm; text-align: center; line-height: 1;
        }

        /* Screen preview */
        .label-grid { display: flex; flex-wrap: wrap; gap: 4mm; }
        .mode-roll  { background: #f3f4f6; padding: 6mm; border-radius: 8px; }
        .sheet-wrap { display: flex; flex-direction: column; align-items: center; gap: 16px; }
        .label-page {
          width: ${A4_W}mm;
          height: ${A4_H}mm;
          overflow: hidden;
          box-sizing: border-box;
          padding: ${Math.max(0, drawTop).toFixed(3)}mm 0 0 ${Math.max(0, drawLeft).toFixed(3)}mm;
          background: #fff;
          box-shadow: 0 1px 6px rgba(0,0,0,0.12);
        }
        .mode-sheet {
          display: grid;
          /* Pitch, not gap: a correction can make a row pitch shorter than the
             label itself, and CSS gaps cannot go negative. */
          grid-template-columns: repeat(${cols}, ${drawColP.toFixed(3)}mm);
          grid-auto-rows: ${drawRowP.toFixed(3)}mm;
          gap: 0;
          flex-wrap: nowrap;
        }

        /* ── Print ── */
        @media print {
          .no-print { display: none !important; }
          html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
          body * { visibility: hidden; }
          .mode-roll, .mode-roll *, .sheet-wrap, .sheet-wrap * { visibility: visible; }

          .label-card { border: ${showBorder ? '0.2mm solid #cbd5e1' : 'none'}; }

          /* Roll / thermal — one label per page */
          .mode-roll { position: absolute; top: 0; left: 0; display: block; padding: 0; background: #fff; }
          .mode-roll .label-card { border: ${showBorder ? '0.2mm solid #cbd5e1' : 'none'}; page-break-after: always; break-after: page; }

          /* A4 sheet — paginated grid, margins reset per page */
          .sheet-wrap { position: absolute; top: 0; left: 0; gap: 0; }
          .label-page { box-shadow: none; page-break-after: always; break-after: page; }
          .label-page.print-last { page-break-after: auto; break-after: auto; }
          .label-page.skip-print { display: none !important; }
        }
        ${mode === 'roll'  ? `@page { size: ${w}mm ${h}mm; margin: 0; }` : ''}
        ${mode === 'sheet' ? `@page { size: A4; margin: 0; }` : ''}
      `}</style>
    </div>
  );
}

function NumField({ label, value, onChange, step }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">{label}</label>
      <input type="number" value={value} step={step || '1'} onChange={e => onChange(e.target.value)}
        className="w-full text-center border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-orange-400" />
    </div>
  );
}
